import { useEffect, useMemo, useRef, useState } from "react"
import { createRoot, type Root } from "react-dom/client"

import { FacilityMapIcon } from "@/components/icons"
import { useIsMobile } from "@/hooks/useIsMobile"
import {
  facilityToCoordinates,
  getFacilitiesMapLocation,
  getFacilitiesWithCoordinates,
  loadYandexMaps,
} from "@/lib/yandex-maps"
import { cn } from "@/lib/utils"
import type { Facility } from "@/types/warehouse"
import type { YandexMapInstance } from "@/types/yandex-maps"

type MarkerEntry = {
  marker: unknown
  root: Root
}

const MAP_ANIMATION = {
  duration: 500,
  easing: "ease-in-out" as const,
}

export type FacilityMapProps = {
  facilities: Facility[]
  isLoading?: boolean
  selectedFacilityCode?: string | null
  onFacilitySelect?: (code: string) => void
  className?: string
}

export function FacilityMap({
  facilities,
  isLoading = false,
  selectedFacilityCode,
  onFacilitySelect,
  className,
}: FacilityMapProps) {
  const isMobile = useIsMobile()
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<YandexMapInstance | null>(null)
  const markersRef = useRef<MarkerEntry[]>([])
  const onSelectRef = useRef(onFacilitySelect)
  const selectedFacilityCodeRef = useRef(selectedFacilityCode)
  const prevSelectedFacilityCodeRef = useRef<string | null | undefined>(
    undefined,
  )

  const [mapStatus, setMapStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  )
  const [mapError, setMapError] = useState<string | null>(null)

  const facilitiesWithCoordinates = useMemo(
    () => getFacilitiesWithCoordinates(facilities),
    [facilities],
  )

  const overviewLocation = useMemo(
    () => getFacilitiesMapLocation(facilitiesWithCoordinates, { compact: isMobile }),
    [facilitiesWithCoordinates, isMobile],
  )

  useEffect(() => {
    onSelectRef.current = onFacilitySelect
  }, [onFacilitySelect])

  useEffect(() => {
    selectedFacilityCodeRef.current = selectedFacilityCode
  }, [selectedFacilityCode])

  useEffect(() => {
    const container = containerRef.current
    if (!container || facilitiesWithCoordinates.length === 0) {
      return
    }

    let cancelled = false

    const clearMarkers = () => {
      for (const { marker, root } of markersRef.current) {
        mapRef.current?.removeChild(marker)
        root.unmount()
      }
      markersRef.current = []
    }

    loadYandexMaps()
      .then((ymaps3) => {
        if (cancelled || !containerRef.current) {
          return
        }

        const {
          YMap,
          YMapDefaultSchemeLayer,
          YMapDefaultFeaturesLayer,
          YMapMarker,
        } = ymaps3

        if (!mapRef.current) {
          const map = new YMap(containerRef.current, {
            location: overviewLocation,
          })
          map.addChild(new YMapDefaultSchemeLayer({}))
          map.addChild(new YMapDefaultFeaturesLayer({}))
          mapRef.current = map
        }

        clearMarkers()

        for (const facility of facilitiesWithCoordinates) {
          const markerElement = document.createElement("button")
          markerElement.type = "button"
          markerElement.className =
            "block cursor-pointer border-0 bg-transparent p-0 leading-none drop-shadow-md"
          markerElement.style.transform = "translate(-50%, -100%)"
          markerElement.setAttribute("aria-label", facility.name)

          const root = createRoot(markerElement)
          root.render(<FacilityMapIcon className="block" />)

          const marker = new YMapMarker(
            {
              coordinates: facilityToCoordinates(facility),
              zIndex: selectedFacilityCode === facility.code ? 2 : 1,
              onClick: () => onSelectRef.current?.(facility.code),
            },
            markerElement,
          )

          mapRef.current.addChild(marker)
          markersRef.current.push({ marker, root })
        }

        setMapStatus("ready")
        setMapError(null)
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return
        }
        setMapStatus("error")
        setMapError(
          error instanceof Error ? error.message : "Ошибка загрузки карты",
        )
      })

    return () => {
      cancelled = true
      clearMarkers()
    }
  }, [facilitiesWithCoordinates, overviewLocation, selectedFacilityCode])

  useEffect(() => {
    if (!mapRef.current || selectedFacilityCode) {
      return
    }

    mapRef.current.update({
      location: overviewLocation,
    })
  }, [overviewLocation, selectedFacilityCode])

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const observer = new ResizeObserver(() => {
      if (!mapRef.current || selectedFacilityCodeRef.current) {
        return
      }

      mapRef.current.update({
        location: overviewLocation,
      })
    })

    observer.observe(container)
    return () => observer.disconnect()
  }, [overviewLocation])

  useEffect(() => {
    if (!mapRef.current) {
      return
    }

    const prevSelected = prevSelectedFacilityCodeRef.current
    prevSelectedFacilityCodeRef.current = selectedFacilityCode ?? null

    if (selectedFacilityCode) {
      const facility = facilitiesWithCoordinates.find(
        (item) => item.code === selectedFacilityCode,
      )
      if (!facility) {
        return
      }

      mapRef.current.update({
        location: {
          center: facilityToCoordinates(facility),
          zoom: 16,
          ...MAP_ANIMATION,
        },
      })
      return
    }

    if (prevSelected === undefined) {
      return
    }

    mapRef.current.update({
      location: {
        ...overviewLocation,
        ...(prevSelected != null ? MAP_ANIMATION : {}),
      },
    })
  }, [facilitiesWithCoordinates, overviewLocation, selectedFacilityCode])

  useEffect(() => {
    return () => {
      for (const { marker, root } of markersRef.current) {
        mapRef.current?.removeChild(marker)
        root.unmount()
      }
      markersRef.current = []
      mapRef.current?.destroy()
      mapRef.current = null
    }
  }, [])

  return (
    <div className={cn("relative h-full w-full overflow-hidden rounded-[8px]", className)}>
      <div ref={containerRef} className="h-full w-full" />

      {(isLoading || mapStatus === "loading") && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-card/60 text-sm text-light-muted">
          Загрузка карты…
        </div>
      )}

      {mapStatus === "error" && (
        <div className="absolute inset-0 flex items-center justify-center bg-card p-4 text-center text-sm text-destructive">
          {mapError}
        </div>
      )}

      {!isLoading &&
        mapStatus !== "error" &&
        facilitiesWithCoordinates.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-card p-4 text-center text-sm text-light-muted">
            Нет складов с координатами для отображения на карте
          </div>
        )}
    </div>
  )
}
